import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Mail, Loader2, Check } from 'lucide-react'
import { assets } from '../config/assets'
import { restorePassword } from '../api/auth'
import UiButton from '../components/ui/UiButton'

export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess(false)
    setIsLoading(true)

    try {
      await restorePassword({ email })
      setSuccess(true)
    } catch (err) {
      setError(err.message || t('forgotPassword.genericError', 'Wystąpił błąd. Spróbuj ponownie.'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative bg-brand-blue px-4 py-12">
      <div
        className="absolute inset-0 bg-cover bg-fixed bg-center pointer-events-none"
        style={{ backgroundImage: `url(${assets.backgroundPattern})` }}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md">
        <Link
          to="/"
          className="block text-center font-climate text-brand-gold text-3xl md:text-4xl mb-8 hover:text-brand-orange transition"
        >
          Abonamenty Literackie
        </Link>

        <div className="bg-white/25 backdrop-blur-md rounded-[2rem] border border-white/10 shadow-xl p-8 md:p-10">
          <h1 className="font-climate text-brand-blue text-3xl mb-2 text-center">
            {t('forgotPassword.title', 'Resetowanie hasła')}
          </h1>
          <p className="text-brand-blue/80 text-center mb-8">
            {t('forgotPassword.subtitle', 'Podaj adres email, a wyślemy Ci link do resetowania hasła.')}
          </p>

          {success && (
            <div className="mb-6 p-4 rounded-2xl bg-green-100/80 text-green-900 text-sm font-medium flex items-start gap-3">
              <Check size={20} className="shrink-0 mt-0.5" />
              <span>{t('forgotPassword.success', 'Jeśli konto istnieje, wysłaliśmy link do resetowania hasła.')}</span>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 rounded-2xl bg-red-100/80 text-red-900 text-sm font-medium">
              {error}
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-bold text-brand-blue mb-2">
                  {t('login.email', 'E-mail')}
                </label>
                <div className="relative">
                  <Mail
                    size={20}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-blue/50"
                    aria-hidden="true"
                  />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full pl-11 pr-4 py-3 rounded-full bg-white/80 border border-white/20 text-brand-blue placeholder:text-brand-blue/40 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                    placeholder={t('login.emailPlaceholder', 'twoj@email.pl')}
                  />
                </div>
              </div>

              <UiButton
                type="submit"
                variant="gold"
                size="lg"
                fullWidth
                disabled={isLoading}
                icon={isLoading ? Loader2 : undefined}
              >
                {isLoading
                  ? t('forgotPassword.sending', 'Wysyłanie...')
                  : t('forgotPassword.submit', 'Wyślij link')}
              </UiButton>
            </form>
          )}

          <div className="mt-6 text-center text-sm">
            <Link
              to="/login"
              className="text-brand-blue hover:text-brand-orange font-bold transition"
            >
              {t('forgotPassword.backToLogin', 'Wróć do logowania')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
